import {
  FC,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useContext,
} from 'react';
import type { MouseEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams, useLocation } from 'react-router';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { SearchInput } from '@/components/ui/search-input';
import { Badge } from '@/components/ui/badge';
import HelpTooltip from '@/components/ui/help-tooltip';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { PreviewBadge } from '@/components/ui/preview-badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { JsonViewer } from '@/components/json-viewer';
import CsvPreviewDialog from '@/features/data-sources/csv-preview-dialog';
import { FilePreviewModal } from '@/features/data-sources/file-preview-modal';
import { isPreviewableMimeType } from '@/utils/preview-supported-mime-types';
import { wpSiteKey } from '@/utils/wp-site-key';
import {
  Table as TableIcon,
  Database,
  CloudUpload,
  Search,
  BotMessageSquare,
  MessageSquareShare,
  Share2,
  Eye,
  Layers,
  FileCode,
} from 'lucide-react';
import { CollectionDocumentsList } from './collection-documents-list';

interface DatasetFileRow extends Source {
  s3_key: string;
}
import { CollectionSkeleton } from '../collection-skeleton';
import { EditCollectionModal } from '../edit-collection-modal';
import {
  RefreshCw,
  MoreVertical,
  Trash,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Edit2,
  Hourglass,
  RotateCcw,
  Filter,
  Download,
  Info,
  Settings,
  Pencil,
} from 'lucide-react';
import { CopyButton } from '@/components/ui/copy-button';
import { Table } from '@/components/table';
import { useNotification } from '@/components/notifications';
import { useDebounce } from '@/hooks/use-debounce';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { DeleteConfirmationModal } from '@/components/delete-confirmation-modal';
import { DeleteCollectionModal } from '@/components/delete-collection-modal';
import { ShareModal } from '@/components/share-modal';
import { getCollectionSources } from '@/services/data-sources/get-collection-sources';
import { deleteSource } from '@/services/data-sources/delete-source';
import {
  deleteCollection,
  hardDeleteCollection,
} from '@/services/data-sources/delete-collection';
import { cancelCollectionDeletion } from '@/services/data-sources/cancel-collection-deletion';
import {
  Collection,
  CollectionSource,
  Source,
  SourceOrder,
  SourceOrderType,
  WordPressExclusionRule,
} from '@/types/source-types';
import { getFileTypeLabel } from '@/utils/get-file-type-label';
import { isAccessDeniedError } from '@/utils/is-access-denied-error';
import { getCollection } from '@/services/data-sources/get-collection';
import {
  useGetCollection,
  collectionQueryKey,
} from '@/hooks/queries/data-sources/use-get-collection';
import { useUpdateCollection } from '@/hooks/mutations/data-sources/use-update-collection';
import { getPresignedDownloadUrl } from '@/services/data-sources/get-presigned-download-url';
import { getDatasets } from '@/services/data-sources/get-datasets';
import { getDataset } from '@/services/data-sources/get-dataset';
import { getDatasetMetadata } from '@/services/data-sources/get-dataset-metadata';
import { createDataset } from '@/services/data-sources/create-dataset';
import { updateDataset } from '@/services/data-sources/update-dataset';
import { deleteDataset } from '@/services/data-sources/delete-dataset';
import {
  useGetSoftDeleteTtl,
  type TimeUnit,
} from '@/hooks/queries/data-sources/use-get-soft-delete-ttl';
import { NewDataSourceModal } from '../new-data-source-modal';
import { EditFileFieldsModal } from '../edit-file-fields-modal';
import { FileInfoModal } from '../file-info-modal';
import { format } from 'date-fns';
import { useDataSourcesStore } from '@/stores/data-sources-store';
import { useOrgStore } from '@/stores/org-store';
import { AuthContext } from '@/context/auth-context';
import { cn } from '@/lib/utils';
import {
  CollectionSearchFilters,
  CollectionSearchFilters as FiltersType,
} from '@/components/collection-search-filters';
import { CollectionSearchResults } from '@/components/collection-search-results';
import {
  searchCollection,
  CollectionSearchResult,
} from '@/services/data-sources/search-collection';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  CSVQuality,
  CollectionSourceConfig,
  Dataset,
  DatasetTableMetadata,
} from '@/types/source-types';
import { DatasetsTable } from '../datasets-table';
import { StructuredUploadGuide } from '../structured-upload-help';
import { LLMPlaygroundCard } from '@/features/datasets/llm-playground/llm-playground-card';
import { CollectionChatCard } from '@/features/collection-chat';
import { CollectionChatPromptModal } from '../collection-chat-prompt-modal';
import { ExternalChatTab } from '@/features/external-chat';
import { UploadApiTab } from '@/features/upload-api';
import { Textarea } from '@/components/ui/textarea';
import { DatasetModal, DatasetFormValues } from '../dataset-modal';
import { ROUTES } from '@/constants/routes';
import {
  useFileSelection,
  useCollectionInfo,
  useChatSettings,
  useCollectionRole,
} from './hooks';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import {
  inferCollectionSource,
  extractExclusionRules,
  extractSiteExclusionRules,
  mapConfigToSitesState,
} from '../wordpress-source-utils';
import { WordPressExclusionModal } from '../wordpress-exclusion-modal';
import { WpSiteLastIndexed } from './components/wp-site-last-indexed';
import {
  WpSiteInlineEditor,
  buildRemoveSiteUpdate,
} from './components/wp-site-inline-editor';
import {
  IngestionStatusBadge,
  DisplayStatus,
} from '@/components/ingestion-status-badge';

const getCollectionSourceLabel = (source?: CollectionSource) =>
  source === 'wordpress_api' ? 'WordPress API' : 'Uploads';

/**
 * Converts a TTL value and unit to milliseconds.
 * Supports minutes, hours, and days units.
 */
const getTtlInMilliseconds = (value: number, unit: TimeUnit): number => {
  const unitMultipliers: Record<TimeUnit, number> = {
    minutes: 60 * 1000,
    hours: 60 * 60 * 1000,
    days: 24 * 60 * 60 * 1000,
  };
  return value * unitMultipliers[unit];
};

/**
 * Formats the time unit string based on count (singular vs plural).
 * Examples: "1 day", "2 days", "1 hour", "5 minutes"
 */
const formatTimeUnit = (count: number, unit: TimeUnit): string => {
  if (count === 1 && unit.endsWith('s')) {
    return unit.slice(0, -1);
  }
  return unit;
};

export const CollectionPage: FC = () => {
  const { collectionId, datasetId } = useParams<{
    collectionId: string;
    datasetId?: string;
  }>();
  const notification = useNotification();
  const {
    isStructuredCollectionsEnabled,
    isCollectionChatEnabled,
    isExternalChatEnabled,
    isUploadApiEnabled,
  } = useLaunchDarkly();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useOrgNavigate();
  const location = useLocation();
  const { setCurrentCollection, setCurrentDataset } = useDataSourcesStore();
  const { currentOrg } = useOrgStore();
  const authToken = useOrgStore((state) => state.token);
  const { isAdmin } = useContext(AuthContext);

  const collectionPath = useMemo(() => {
    if (!collectionId) return ROUTES.DATA_SOURCES.ROOT;
    return ROUTES.DATA_SOURCES.COLLECTIONS.replace(
      ':collectionId',
      collectionId,
    );
  }, [collectionId]);

  const datasetsPath = useMemo(() => {
    if (!collectionId) return ROUTES.DATA_SOURCES.ROOT;
    return ROUTES.DATA_SOURCES.COLLECTIONS_DATASETS.replace(
      ':collectionId',
      collectionId,
    );
  }, [collectionId]);

  const searchString = useMemo(() => searchParams.toString(), [searchParams]);

  const buildPathWithSearch = useCallback(
    (path: string) => (searchString ? `${path}?${searchString}` : path),
    [searchString],
  );

  const currentPathWithSearch = useMemo(
    () => location.pathname + (location.search || ''),
    [location.pathname, location.search],
  );

  const [sources, setSources] = useState<Source[]>([]);
  const [filteredSources, setFilteredSources] = useState<Source[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSource, setSelectedSource] = useState<Source | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  // Pending WordPress site removal (confirm dialog). Holds the target site id +
  // the pre-built next config so the confirm handler just persists it.
  const [pendingSiteRemoval, setPendingSiteRemoval] = useState<{
    siteId: string;
    label: string;
  } | null>(null);
  const [isRemovingSite, setIsRemovingSite] = useState(false);

  const orgId = currentOrg?.orgid;
  const queryClient = useQueryClient();
  const {
    data: collectionData,
    isLoading: collectionQueryLoading,
    error: collectionError,
  } = useGetCollection({ collectionId, orgId });
  const collection = collectionData ?? null;
  // isLoading is true only on the first fetch with an empty cache; background
  // refetches (post-save invalidation) surface as isFetching, which we do NOT
  // gate the skeleton on — so the page never unmounts mid-edit. This replaces
  // the manual collectionLoading flag + the { silent } refetch flag.
  const collectionLoading = collectionQueryLoading;

  const { mutateAsync: mutateCollection } = useUpdateCollection({ orgId });

  // Optimistic writer for callers that used to setCollection(prev => ...).
  // Writing the cache keeps the query the single source of truth.
  const patchCollectionCache = useCallback(
    (updater: (prev: Collection | null) => Collection | null) => {
      queryClient.setQueryData<Collection>(
        collectionQueryKey(orgId, collectionId),
        (prev) => updater(prev ?? null) ?? undefined,
      );
    },
    [queryClient, orgId, collectionId],
  );

  // Refetch the collection after an external mutation (edit modal, exclusion
  // modal, external-chat tab) that wrote via its own service call.
  const refetchCollection = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: collectionQueryKey(orgId, collectionId),
    });
  }, [queryClient, orgId, collectionId]);

  const { canEdit, canShare, canDelete } = useCollectionRole(collection);
  const [isNewDataSourceModalOpen, setIsNewDataSourceModalOpen] =
    useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCollectionDeleteModalOpen, setIsCollectionDeleteModalOpen] =
    useState(false);
  const [isCollectionDeleteLoading, setIsCollectionDeleteLoading] =
    useState(false);
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const initialTab =
    datasetId || location.pathname.endsWith('/datasets') ? 'datasets' : 'files';
  const [activeTab, setActiveTab] = useState<'files' | 'datasets'>(initialTab);
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [filteredDatasets, setFilteredDatasets] = useState<Dataset[]>([]);
  const [datasetsLoading, setDatasetsLoading] = useState(false);
  const [isDatasetModalOpen, setIsDatasetModalOpen] = useState(false);
  const [datasetModalMode, setDatasetModalMode] = useState<'create' | 'edit'>(
    'create',
  );
  const [datasetDraft, setDatasetDraft] = useState<Dataset | null>(null);
  const [datasetModalFocusKey, setDatasetModalFocusKey] = useState<
    string | null
  >(null);
  const [datasetDeleteTarget, setDatasetDeleteTarget] =
    useState<Dataset | null>(null);
  const [isDatasetSubmitting, setIsDatasetSubmitting] = useState(false);
  const [isDatasetDeleting, setIsDatasetDeleting] = useState(false);
  const [datasetDetail, setDatasetDetail] = useState<Dataset | null>(null);
  const [datasetDetailLoading, setDatasetDetailLoading] = useState(false);
  const [datasetFilesLoading, setDatasetFilesLoading] = useState(false);
  const [datasetMetadataTables, setDatasetMetadataTables] = useState<
    DatasetTableMetadata[]
  >([]);
  const [datasetMetadataLoading, setDatasetMetadataLoading] = useState(false);
  const [datasetMetadataError, setDatasetMetadataError] = useState<
    string | null
  >(null);
  const [qualityShouldLoad, setQualityShouldLoad] = useState(false);
  const [previewMetadata, setPreviewMetadata] =
    useState<DatasetTableMetadata | null>(null);
  const qualitySectionRef = useRef<HTMLDivElement | null>(null);
  const accessDeniedRef = useRef(false);
  // File selection - uses extracted hook
  const {
    selectedFiles,
    selectedFileNames,
    selectedFilesCount,
    hasSelection,
    headerCheckboxState,
    toggleFileSelection,
    toggleSelectAllVisible,
    setSelectedFiles,
  } = useFileSelection({ sources, filteredSources });
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [isBulkDeleteLoading, setIsBulkDeleteLoading] = useState(false);
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const [previewCsv, setPreviewCsv] = useState<{
    key: string;
    filename: string;
  } | null>(null);
  const [filePreview, setFilePreview] = useState<{
    filename: string;
    contentType: string | null | undefined;
  } | null>(null);
  const [isCollectionInfoModalOpen, setIsCollectionInfoModalOpen] =
    useState(false);
  const [isEditFileFieldsModalOpen, setIsEditFileFieldsModalOpen] =
    useState(false);
  const [isFileInfoModalOpen, setIsFileInfoModalOpen] = useState(false);
  const [isWpExclusionModalOpen, setIsWpExclusionModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  // Tracks which site's exclusion modal is open (site_id or null)
  const [wpExclusionSiteId, setWpExclusionSiteId] = useState<string | null>(
    null,
  );
  // Per-site last indexed info
  const [sitesLastIndexed, setSitesLastIndexed] = useState<
    Record<
      string,
      import('@/services/data-sources/get-wordpress-sites-last-indexed').SiteLastIndexedInfo
    >
  >({});

  // Fetch TTL configuration for soft delete (only when collection is pending deletion)
  const { data: ttlConfig } = useGetSoftDeleteTtl({
    orgId: currentOrg?.orgid,
    enabled: collection?.status === 'pending_deletion',
  });
  const ttlValue = ttlConfig?.value ?? 7;
  const ttlUnit = ttlConfig?.unit ?? 'days';

  // Helper to determine display status from S3 indexing_status tag
  const getDisplayStatus = useCallback(
    (indexingStatus?: string, _ingestionMethod?: string): DisplayStatus => {
      // Only use indexing_status - this is the single source of truth
      if (!indexingStatus) return 'unknown';

      // Normalize status to lowercase for case-insensitive matching
      const normalizedStatus = indexingStatus.toLowerCase();

      const statusMap: Record<string, DisplayStatus> = {
        queued: 'pending', // Upload response returns "queued"
        pending: 'pending',
        processing: 'started',
        'in progress': 'started', // Old lambda-based status
        complete: 'success',
        error: 'failure',
      };

      return statusMap[normalizedStatus] || 'unknown';
    },
    [],
  );

  const collectionSource = inferCollectionSource(collection);
  const sourceConfig = collection?.source_config;
  const isUnstructuredQdrantCollection =
    (collection?.data_type || '').toLowerCase() === 'unstructured' &&
    (collection?.destination || '').toLowerCase() === 'qdrant';
  const canEditFileFields =
    canEdit &&
    isUnstructuredQdrantCollection &&
    (collection?.exposed_upload_attributes?.length ?? 0) > 0;

  // Collection info (chunk count) - uses extracted hook
  const {
    collectionInfo,
    collectionInfoLoading,
    formattedPointsCount,
    fetchCollectionInfo,
  } = useCollectionInfo({ collectionId, isUnstructuredQdrantCollection });

  // Chat settings - uses extracted hook
  const {
    isChatPromptModalOpen,
    setIsChatPromptModalOpen,
    handleChatPromptUpdated,
    isChatFiltersModalOpen,
    setIsChatFiltersModalOpen,
    chatFiltersText,
    setChatFiltersText,
    chatFiltersError,
    setChatFiltersError,
    isChatFiltersSaving,
    handleOpenChatFilters,
    handleSaveChatFilters,
    handleSavePrompt,
    handleDeletePrompt,
    handleReorderPrompts,
  } = useChatSettings({ collection, collectionId, patchCollectionCache });

  const collectionConfig = (collection?.config ?? null) as
    | (Record<string, unknown> & { chat_system_prompt?: string | null })
    | null;
  const hasCustomChatPrompt = Boolean(collectionConfig?.chat_system_prompt);
  const isWordPressCollection =
    collection?.data_type !== 'structured' &&
    collectionSource === 'wordpress_api';
  const wpExclusionRules = useMemo(
    () => extractExclusionRules(collection),
    [collection],
  );

  const wordPressSites = useMemo(
    () => mapConfigToSitesState(sourceConfig),
    [sourceConfig],
  );
  const isMultiSite =
    wordPressSites.length > 1 ||
    (sourceConfig?.wordpress_sites && sourceConfig.wordpress_sites.length > 0);

  const wpSiteDomains = useMemo(() => {
    if (!isMultiSite) return [];
    // Deduped: duplicate configured base_urls would render repeated checkboxes
    // in the search site filter.
    return [
      ...new Set(
        wordPressSites
          .map((s) => wpSiteKey(s.baseUrl))
          .filter((k): k is string => Boolean(k)),
      ),
    ];
  }, [isMultiSite, wordPressSites]);

  // Fetch last indexed info for WordPress sites
  useEffect(() => {
    if (!isWordPressCollection || !collectionId) {
      setSitesLastIndexed({});
      return;
    }
    let cancelled = false;
    // Reset before fetching to avoid showing stale data while loading
    setSitesLastIndexed({});
    (async () => {
      try {
        const { getWordPressSitesLastIndexed } = await import(
          '@/services/data-sources/get-wordpress-sites-last-indexed'
        );
        const result = await getWordPressSitesLastIndexed(collectionId);
        if (!cancelled) {
          if (result.ok) {
            setSitesLastIndexed(result.data.sites);
          } else {
            console.warn('Failed to fetch sites last indexed:', result.detail);
            setSitesLastIndexed({});
          }
        }
      } catch (err) {
        console.warn('Failed to fetch sites last indexed', err);
        if (!cancelled) {
          setSitesLastIndexed({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isWordPressCollection, collectionId, wordPressSites]);

  // Per-site exclusion modal support
  const wpExclusionActiveSite = useMemo(
    () =>
      wpExclusionSiteId
        ? (wordPressSites.find((s) => s.siteId === wpExclusionSiteId) ?? null)
        : null,
    [wpExclusionSiteId, wordPressSites],
  );

  const wpExclusionModalRules = useMemo(
    () =>
      wpExclusionSiteId
        ? extractSiteExclusionRules(collection, wpExclusionSiteId)
        : wpExclusionRules,
    [wpExclusionSiteId, collection, wpExclusionRules],
  );
  // Per-site connection testing now lives inside WordPressSiteForm (the inline
  // editor's expanded card), so the page-level test handlers were removed.

  const handleTabChange = useCallback(
    (value: string) => {
      const tab: 'files' | 'datasets' =
        value === 'datasets' ? 'datasets' : 'files';

      if (tab === 'datasets' && collection?.data_type === 'unstructured') {
        if (activeTab !== 'files') {
          setActiveTab('files');
        }
        if (collectionId) {
          const targetPath = buildPathWithSearch(collectionPath);
          if (currentPathWithSearch !== targetPath) {
            navigate(targetPath, { replace: true });
          }
        }
        return;
      }

      setActiveTab(tab);

      if (!collectionId) return;
      const targetPath = tab === 'datasets' ? datasetsPath : collectionPath;
      const fullTarget = buildPathWithSearch(targetPath);
      if (currentPathWithSearch !== fullTarget) {
        navigate(fullTarget);
      }
    },
    [
      activeTab,
      buildPathWithSearch,
      collection?.data_type,
      collectionId,
      collectionPath,
      currentPathWithSearch,
      datasetsPath,
      navigate,
    ],
  );

  // Search-related state
  const [searchResults, setSearchResults] = useState<CollectionSearchResult[]>(
    [],
  );
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string>('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  // Upload API docs now live as a collapsible at the top of the Files tab
  // (no longer a primary nav tab). Default open when deep-linked via
  // ?upload-api=1 so existing links still land on the docs.
  const [uploadApiOpen, setUploadApiOpen] = useState(
    () => searchParams.get('upload-api') === '1',
  );
  // Deep-link requested tab via URL params (?search=1, ?chat=1, ?external-chat=1).
  const requestedTabFromUrl = useMemo(() => {
    if (searchParams.get('external-chat') === '1') return 'external-chat';
    if (searchParams.get('chat') === '1') return 'chat';
    if (searchParams.get('search') === '1') return 'search';
    if (searchParams.get('records') === '1') return 'records';
    if (searchParams.get('wordpress-settings') === '1')
      return 'wordpress-settings';
    return null;
  }, [searchParams]);

  const availableMainTabs = useMemo(() => {
    const s = new Set(['files']);
    if (isWordPressCollection) s.add('wordpress-settings');
    if (collection?.data_type !== 'structured') s.add('search');
    if (isUnstructuredQdrantCollection) s.add('records');
    if (isUnstructuredQdrantCollection && isCollectionChatEnabled)
      s.add('chat');
    if (isUnstructuredQdrantCollection && isExternalChatEnabled)
      s.add('external-chat');
    return s;
  }, [
    collection?.data_type,
    isWordPressCollection,
    isUnstructuredQdrantCollection,
    isCollectionChatEnabled,
    isExternalChatEnabled,
  ]);

  // Single source of truth = URL. Before `collection` loads, trust the URL
  // param so a cold `?external-chat=1` / `?chat=1` link survives the render
  // gap. After load, fall back to 'files' when the requested tab isn't
  // available for this collection (e.g. structured collections, disabled
  // flags).
  const mainTab = useMemo(() => {
    if (requestedTabFromUrl) {
      if (!collection) return requestedTabFromUrl;
      return availableMainTabs.has(requestedTabFromUrl)
        ? requestedTabFromUrl
        : 'files';
    }
    return 'files';
  }, [requestedTabFromUrl, collection, availableMainTabs]);

  const handleMainTabChange = useCallback(
    (next: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.delete('chat');
          params.delete('external-chat');
          params.delete('search');
          params.delete('records');
          params.delete('wordpress-settings');
          if (next === 'chat') params.set('chat', '1');
          else if (next === 'external-chat') params.set('external-chat', '1');
          else if (next === 'search') params.set('search', '1');
          else if (next === 'records') params.set('records', '1');
          else if (next === 'wordpress-settings')
            params.set('wordpress-settings', '1');
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const orderBy =
    (searchParams.get('orderBy') as SourceOrderType) || SourceOrder.DATE_DESC;

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Filter sources and datasets based on search query
  const handleSearch = useCallback(() => {
    if (!debouncedSearchQuery.trim()) {
      setFilteredSources(sources);
      setFilteredDatasets(datasets);
      return;
    }

    const searchLower = debouncedSearchQuery.toLowerCase();

    // Filter based on active tab
    if (activeTab === 'files') {
      // Filter sources by filename
      const filteredSources = sources.filter((source) => {
        // Hide .metadata.json files from non-admin users
        if (!isAdmin && source.filename.endsWith('.metadata.json')) {
          return false;
        }
        return source.filename.toLowerCase().includes(searchLower);
      });
      setFilteredSources(filteredSources);
      setFilteredDatasets(datasets); // Keep all datasets when on files tab
    } else {
      // Filter datasets by name and description
      const filteredDatasets = datasets.filter(
        (dataset) =>
          dataset.name.toLowerCase().includes(searchLower) ||
          dataset.description?.toLowerCase().includes(searchLower),
      );
      setFilteredDatasets(filteredDatasets);

      // Also filter metadata.json files from sources on datasets tab for non-admin users
      const filteredSourcesForDatasets = isAdmin
        ? sources
        : sources.filter(
            (source) => !source.filename.endsWith('.metadata.json'),
          );
      setFilteredSources(filteredSourcesForDatasets);
    }
  }, [sources, datasets, debouncedSearchQuery, activeTab, isAdmin]);

  useEffect(() => {
    handleSearch();
  }, [handleSearch]);

  // Handle sorting
  const handleSort = useCallback(
    (column: string) => {
      const newOrderBy = (() => {
        switch (column) {
          case 'filename':
            return orderBy === SourceOrder.NAME_ASC
              ? SourceOrder.NAME_DESC
              : SourceOrder.NAME_ASC;
          case 'size':
            return orderBy === SourceOrder.SIZE_ASC
              ? SourceOrder.SIZE_DESC
              : SourceOrder.SIZE_ASC;
          case 'last_modified':
            return orderBy === SourceOrder.DATE_ASC
              ? SourceOrder.DATE_DESC
              : SourceOrder.DATE_ASC;
          default:
            return orderBy;
        }
      })();

      // Update URL with new orderBy parameter
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set('orderBy', newOrderBy);
        return newParams;
      });
    },
    [orderBy, setSearchParams],
  );

  // Fetch collection sources
  const fetchSources = useCallback(
    async (refresh = false) => {
      if (!collectionId) return;

      if (refresh) setRefreshing(true);

      try {
        setLoading(true);
        const response = await getCollectionSources({
          collection: collectionId,
          orderBy,
          includeQuality: true,
        });

        if (response?.detail) {
          if (
            response.detail.toLowerCase().includes('not found') ||
            response.detail.toLowerCase().includes('404') ||
            isAccessDeniedError(response.detail)
          ) {
            if (accessDeniedRef.current) return;
            accessDeniedRef.current = true;
            notification.error(
              isAccessDeniedError(response.detail)
                ? 'You do not have access to this collection'
                : 'Collection not found',
            );
            navigate(ROUTES.DATA_SOURCES.ROOT);
            return;
          }
          notification.error(response.detail);
          setSources([]);
        } else {
          // Filter out default collection info file like in source-table.tsx
          // Also filter out .metadata.json files for non-admin users
          const filteredSources =
            response?.data?.files?.filter((source) => {
              if (source.filename.toLowerCase() === '.collection_info') {
                return false;
              }
              if (!isAdmin && source.filename.endsWith('.metadata.json')) {
                return false;
              }
              return true;
            }) || [];
          setSources(filteredSources);
        }
      } catch (error) {
        console.error('Error fetching collection sources:', error);
        notification.error('Failed to fetch collection sources');
        setSources([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [collectionId, notification, orderBy, navigate],
  );

  const deriveObjectKey = useCallback(
    (source: Source) => {
      if (source.key) return source.key;
      if (source.s3_url) {
        try {
          const url = new URL(source.s3_url);
          const path = decodeURIComponent(url.pathname.replace(/^\//, ''));
          if (path) {
            return path;
          }
        } catch (error) {
          console.warn('Failed to derive key from s3_url', error);
        }
      }
      const prefix = collection?.s3_prefix?.replace(/\/+$/, '') ?? '';
      return prefix ? `${prefix}/${source.filename}` : source.filename;
    },
    [collection?.s3_prefix],
  );

  const csvSources = useMemo(
    () =>
      sources.filter((source) =>
        source.filename.toLowerCase().endsWith('.csv'),
      ),
    [sources],
  );

  const datasetCsvOptions = useMemo(
    () =>
      csvSources.map((source) => ({
        key: deriveObjectKey(source),
        label: source.filename,
        sizeMb: source.size_mb,
        quality: source.quality ?? undefined,
      })),
    [csvSources, deriveObjectKey],
  );

  const sourceLookup = useMemo(() => {
    const map = new Map<string, Source>();
    csvSources.forEach((source) => {
      map.set(deriveObjectKey(source), source);
    });
    return map;
  }, [csvSources, deriveObjectKey]);

  // Mirror the query result into the data-sources store (breadcrumbs, header).
  useEffect(() => {
    if (collection) setCurrentCollection(collection);
  }, [collection, setCurrentCollection]);

  // Handle a failed collection load. useGetCollection throws the server detail;
  // not-found / access-denied bounce back to the list, other errors just toast.
  // accessDeniedRef guards against a double toast on rapid re-renders.
  //
  // Only act on an INITIAL-load failure (no cached collection yet). A background
  // refetch (post-save invalidation) that fails while cached data is still on
  // screen must not toast or navigate the user away mid-edit — TanStack keeps
  // the last good data and the query stays stale-marked for the next refetch.
  useEffect(() => {
    if (!collectionError || collection) {
      if (!collectionError) accessDeniedRef.current = false;
      return;
    }
    const message =
      collectionError instanceof Error ? collectionError.message : '';
    const isNotFoundOrDenied =
      message.toLowerCase().includes('not found') ||
      message.toLowerCase().includes('404') ||
      isAccessDeniedError(message);
    if (isNotFoundOrDenied) {
      if (accessDeniedRef.current) return;
      accessDeniedRef.current = true;
      notification.error(
        isAccessDeniedError(message)
          ? 'You do not have access to this collection'
          : 'Collection not found',
      );
      navigate(ROUTES.DATA_SOURCES.ROOT);
      return;
    }
    notification.error(message || 'Failed to fetch collection');
  }, [collectionError, collection, notification, navigate]);

  const handleSaveSiteConfig = useCallback(
    async (nextConfig: CollectionSourceConfig) => {
      if (!collectionId) return;
      try {
        // mutateAsync throws on a { detail } response and awaits the
        // invalidation refetch, so a subsequent per-site save rebuilds from the
        // freshly persisted config — no manual refetch or race guard needed.
        await mutateCollection({
          id: collectionId,
          sourceConfig: nextConfig,
        });
        notification.success('Site configuration saved');
      } catch (error) {
        // Surface the server's detail so the user can fix bad input; fall back
        // to the generic message when there's nothing useful to show. Re-throw
        // so the inline editor keeps the draft instead of clearing it as saved.
        const detail = error instanceof Error ? error.message : '';
        notification.error(
          detail || 'Failed to save site configuration. Please try again.',
        );
        throw error;
      }
    },
    [collectionId, mutateCollection, notification],
  );

  // Stage a site removal by id only. The removal payload is rebuilt from the
  // latest stored config at confirm time, so an inline save or reconcile toggle
  // made while the dialog is open is never overwritten by a stale snapshot.
  const handleRequestRemoveSite = useCallback(
    (siteId: string) => {
      const label =
        wordPressSites.find((s) => s.siteId === siteId)?.label || 'this site';
      setPendingSiteRemoval({ siteId, label });
    },
    [wordPressSites],
  );

  const handleConfirmRemoveSite = useCallback(async () => {
    if (!pendingSiteRemoval) return;
    setIsRemovingSite(true);
    try {
      // Read the authoritative config from the server right before rebuilding.
      // React `collection` state can lag an inline site save that just landed
      // (PATCH done, refetch pending); dropping the target from that stale
      // snapshot would revert the sibling edit on this PATCH.
      const latest = collectionId
        ? await getCollection(collectionId)
        : undefined;
      const baseConfig =
        (latest && !latest.detail
          ? (latest as Collection).source_config
          : undefined) ?? collection?.source_config;
      const nextConfig = buildRemoveSiteUpdate(
        baseConfig,
        pendingSiteRemoval.siteId,
      );
      await handleSaveSiteConfig(nextConfig);
      setPendingSiteRemoval(null);
    } catch {
      // handleSaveSiteConfig already surfaced the error; keep the dialog open.
    } finally {
      setIsRemovingSite(false);
    }
  }, [pendingSiteRemoval, collection, collectionId, handleSaveSiteConfig]);

  const handleSiteSaveExclusionRules = useCallback(
    async (siteId: string, rules: WordPressExclusionRule[]) => {
      if (!collectionId) return;
      try {
        // Read the authoritative config from the server right before merging.
        // React `collection` state can lag a just-persisted inline site save
        // (PATCH done, refetch not yet landed); rebuilding wordpress_sites from
        // that stale snapshot would revert the prior per-site edit on PATCH.
        const latest = await getCollection(collectionId);
        const existingConfig =
          (latest && !latest.detail
            ? (latest as Collection).source_config
            : undefined) ?? collection?.source_config;
        const currentSites = existingConfig?.wordpress_sites || [];
        const updatedSites = currentSites.map((s) =>
          s.site_id === siteId
            ? {
                ...s,
                wordpress_exclusion_rules: rules.length > 0 ? rules : null,
              }
            : s,
        );
        // mutateAsync throws on a { detail } response and awaits the refetch.
        await mutateCollection({
          id: collectionId,
          sourceConfig: {
            ...(existingConfig ?? {}),
            wordpress_sites: updatedSites,
          },
        });
      } catch (error) {
        const detail = error instanceof Error ? error.message : '';
        notification.error(
          detail || 'Failed to save content filters. Please try again.',
        );
      }
    },
    [collection, collectionId, mutateCollection, notification],
  );

  useEffect(() => {
    fetchSources();
  }, [fetchSources, orderBy]);

  // Poll for status updates when files are being ingested - only fetches pending/processing files
  // to avoid performance issues with large collections
  useEffect(() => {
    const hasInProgressFiles = sources.some((source) => {
      if (source.tags?.ingestion_method !== 'celery') return false;

      const status = source.tags?.indexing_status?.toLowerCase();
      return (
        status === 'queued' ||
        status === 'pending' ||
        status === 'processing' ||
        status === 'in progress'
      );
    });

    if (!hasInProgressFiles) return;

    const interval = setInterval(async () => {
      try {
        // Fetch files with both pending and "in progress" status
        // Backend only supports single tag filter, so we make two calls
        const [pendingResponse, processingResponse] = await Promise.all([
          getCollectionSources({
            collection: collectionId || '',
            orderBy,
            includeQuality: true,
            s3TagsFilter: 'indexing_status:pending',
          }),
          getCollectionSources({
            collection: collectionId || '',
            orderBy,
            includeQuality: true,
            s3TagsFilter: 'indexing_status:In progress',
          }),
        ]);

        const inProgressFiles = [
          ...(pendingResponse?.data?.files || []),
          ...(processingResponse?.data?.files || []),
        ];

        if (inProgressFiles.length === 0) {
          // All files completed, do a full refresh and stop polling
          fetchSources(true);
          fetchCollectionInfo(); // Refresh chunk count after ingestion completes
          return;
        }

        // Update sources by merging in-progress file statuses
        setSources((currentSources) =>
          currentSources.map((source) => {
            const updatedFile = inProgressFiles.find(
              (pf) => pf.filename === source.filename,
            );
            return updatedFile || source;
          }),
        );
      } catch (error) {
        console.error('Error polling in-progress files:', error);
      }
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(interval);
  }, [sources, collectionId, orderBy, fetchSources, fetchCollectionInfo]);

  const fetchDatasetsList = useCallback(async () => {
    if (!collectionId || collection?.data_type !== 'structured') {
      setDatasets([]);
      setDatasetsLoading(false);
      return;
    }

    try {
      setDatasetsLoading(true);
      const result = await getDatasets(collectionId);
      if (Array.isArray(result)) {
        setDatasets(result);
      } else if (result?.detail) {
        notification.error(result.detail);
      }
    } catch (error) {
      console.error('Failed to load datasets:', error);
      notification.error('Failed to load datasets');
    } finally {
      setDatasetsLoading(false);
    }
  }, [collection?.data_type, collectionId, notification]);

  useEffect(() => {
    fetchDatasetsList();
  }, [fetchDatasetsList]);

  useEffect(() => {
    if (collection?.data_type === 'structured') {
      setShowSearchResults(false);
    }
  }, [collection?.data_type]);

  useEffect(() => {
    if (!datasetId) {
      setDatasetDetail(null);
      setCurrentDataset(null);
      setDatasetDetailLoading(false);
      setDatasetMetadataTables([]);
      setDatasetMetadataError(null);
      setQualityShouldLoad(false);
      setPreviewMetadata(null);
      setPreviewCsv(null);
      setDatasetDetailRows([]);
      setDatasetFilesLoading(false);
      return;
    }

    if (!collectionId) return;

    if (collection == null || collection.data_type == null) {
      return;
    }

    if (collection.data_type !== 'structured') {
      const targetPath = buildPathWithSearch(collectionPath);
      if (currentPathWithSearch !== targetPath) {
        navigate(targetPath, { replace: true });
      }
      return;
    }

    const fetchDataset = async () => {
      try {
        setDatasetDetailLoading(true);
        const result = await getDataset(collectionId, datasetId);
        if (!result || (result as { detail?: string })?.detail) {
          notification.error(
            (result as { detail?: string })?.detail ||
              'Dataset not found or has been removed',
          );
          const targetPath = buildPathWithSearch(datasetsPath);
          if (currentPathWithSearch !== targetPath) {
            navigate(targetPath, { replace: true });
          }
          setDatasetDetail(null);
          setCurrentDataset(null);
          return;
        }
        const datasetData = result as Dataset;
        setDatasetDetail(datasetData);
        setCurrentDataset(datasetData);
        setQualityShouldLoad(false);
        setPreviewMetadata(null);
        setPreviewCsv(null);
        setDatasetMetadataTables([]);
        setDatasetMetadataError(null);
        setDatasetMetadataLoading(false);

        setDatasetFilesLoading(true);
        const rows = datasetData.csv_object_keys
          .map((reference) => {
            const key = reference?.s3_key;
            if (!key) {
              return null;
            }

            const matched = sourceLookup.get(key);
            if (matched) {
              return {
                ...matched,
                s3_key: key,
              } as DatasetFileRow;
            }

            const filename = key.split('/').pop() || key;
            return {
              filename,
              original_filename: filename,
              s3_url: '',
              content_type: null,
              size_mb: null,
              key,
              metadata: null,
              size: null,
              upload_timestamp: '',
              tags: undefined,
              s3_key: key,
            } as DatasetFileRow;
          })
          .filter((value): value is DatasetFileRow => Boolean(value));
        setDatasetDetailRows(rows);
        setDatasetFilesLoading(false);
      } catch (error) {
        console.error('Failed to fetch dataset detail:', error);
        notification.error('Failed to fetch dataset detail');
        const targetPath = buildPathWithSearch(datasetsPath);
        if (currentPathWithSearch !== targetPath) {
          navigate(targetPath, { replace: true });
        }
        setDatasetDetail(null);
        setCurrentDataset(null);
        setPreviewCsv(null);
        setDatasetDetailRows([]);
        setDatasetFilesLoading(false);
      } finally {
        setDatasetDetailLoading(false);
      }
    };

    fetchDataset();
  }, [
    buildPathWithSearch,
    collection,
    collectionId,
    collectionPath,
    currentPathWithSearch,
    datasetId,
    datasetsPath,
    navigate,
    notification,
    sourceLookup,
    setCurrentDataset,
  ]);

  useEffect(() => {
    if (!datasetDetail || qualityShouldLoad) {
      return;
    }

    const element = qualitySectionRef.current;
    if (!element) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setQualityShouldLoad(true);
            observer.disconnect();
          }
        });
      },
      {
        root: null,
        rootMargin: '200px',
        threshold: 0.1,
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [datasetDetail, qualityShouldLoad]);

  useEffect(() => {
    if (!collectionId || !datasetDetail?.id) {
      setDatasetMetadataTables([]);
      setDatasetMetadataError(null);
      setDatasetMetadataLoading(false);
      return;
    }

    if (!qualityShouldLoad) {
      return;
    }

    let cancelled = false;

    const loadMetadata = async () => {
      try {
        setDatasetMetadataLoading(true);
        const result = await getDatasetMetadata(collectionId, datasetDetail.id);

        if ('detail' in result) {
          if (!cancelled) {
            setDatasetMetadataTables([]);
            setDatasetMetadataError(
              result.detail || 'Failed to fetch dataset metadata',
            );
          }
          return;
        }

        if (!cancelled) {
          setDatasetMetadataTables(result.tables || []);
          setDatasetMetadataError(null);
        }
      } catch (error) {
        console.error('Failed to fetch dataset metadata:', error);
        if (!cancelled) {
          setDatasetMetadataTables([]);
          setDatasetMetadataError('Failed to fetch dataset metadata');
        }
      } finally {
        if (!cancelled) {
          setDatasetMetadataLoading(false);
        }
      }
    };

    loadMetadata();

    return () => {
      cancelled = true;
    };
  }, [collectionId, datasetDetail?.id, qualityShouldLoad]);

  useEffect(() => {
    const wantsDatasets =
      location.pathname.includes('/datasets') || Boolean(datasetId);

    if (wantsDatasets && collection?.data_type === 'unstructured') {
      if (collectionId) {
        const targetPath = buildPathWithSearch(collectionPath);
        if (currentPathWithSearch !== targetPath) {
          navigate(targetPath, { replace: true });
        }
      }
      if (activeTab !== 'files') {
        setActiveTab('files');
      }
      return;
    }

    const nextTab: 'files' | 'datasets' = wantsDatasets ? 'datasets' : 'files';
    if (activeTab !== nextTab) {
      setActiveTab(nextTab);
    }
  }, [
    activeTab,
    buildPathWithSearch,
    collection?.data_type,
    collectionId,
    collectionPath,
    currentPathWithSearch,
    datasetId,
    location.pathname,
    navigate,
  ]);

  const datasetModalInitialValues = useMemo(() => {
    if (datasetModalMode === 'edit' && datasetDraft) {
      return {
        name: datasetDraft.name,
        description: datasetDraft.description,
        csv_object_keys: datasetDraft.csv_object_keys,
      };
    }
    return null;
  }, [datasetDraft, datasetModalMode]);

  const handleDatasetModalClose = useCallback(() => {
    setIsDatasetModalOpen(false);
    setDatasetDraft(null);
    setDatasetModalFocusKey(null);
  }, []);

  const handleOpenCreateDataset = useCallback(() => {
    handleTabChange('datasets');
    setDatasetModalMode('create');
    setDatasetDraft(null);
    setDatasetModalFocusKey(null);
    setIsDatasetModalOpen(true);
  }, [handleTabChange]);

  const handleEditDataset = useCallback(
    (dataset: Dataset, options?: { focusKey?: string | null }) => {
      setDatasetModalMode('edit');
      setDatasetDraft(dataset);
      setDatasetModalFocusKey(options?.focusKey ?? null);
      if (!datasetId || datasetId !== dataset.id) {
        handleTabChange('datasets');
      }
      setIsDatasetModalOpen(true);
    },
    [datasetId, handleTabChange],
  );

  const handleDatasetDeleteRequest = useCallback((dataset: Dataset) => {
    setDatasetDeleteTarget(dataset);
  }, []);

  const handleOpenDatasetDetail = useCallback(
    (dataset: Dataset) => {
      if (!collectionId) return;
      const detailPath = ROUTES.DATA_SOURCES.COLLECTION_DATASET_DETAIL.replace(
        ':collectionId',
        collectionId,
      ).replace(':datasetId', dataset.id);
      const fullPath = buildPathWithSearch(detailPath);
      if (currentPathWithSearch !== fullPath) {
        navigate(fullPath);
      }
    },
    [buildPathWithSearch, collectionId, currentPathWithSearch, navigate],
  );

  const handleDatasetSubmit = useCallback(
    async (values: DatasetFormValues) => {
      if (!collectionId) return;

      setIsDatasetSubmitting(true);

      try {
        if (datasetModalMode === 'create') {
          const result = await createDataset(collectionId, values);
          if (!result || (result as { detail?: string })?.detail) {
            notification.error(
              (result as { detail?: string })?.detail ||
                'Failed to create dataset',
            );
            return;
          }
          notification.success('Dataset created successfully');
          const created = result as Dataset;
          if (datasetId && datasetId === created.id) {
            setDatasetDetail(created);
            setCurrentDataset(created);
          }
        } else if (datasetDraft) {
          const result = await updateDataset(
            collectionId,
            datasetDraft.id,
            values,
          );
          if (!result || (result as { detail?: string })?.detail) {
            notification.error(
              (result as { detail?: string })?.detail ||
                'Failed to update dataset',
            );
            return;
          }
          notification.success('Dataset updated successfully');
          const updated = result as Dataset;
          if (datasetId && datasetDraft.id === datasetId) {
            setDatasetDetail(updated);
            setCurrentDataset(updated);
          }
        }

        await fetchDatasetsList();
        handleDatasetModalClose();
      } catch (error) {
        console.error('Dataset save error:', error);
        notification.error('Failed to save dataset');
      } finally {
        setIsDatasetSubmitting(false);
      }
    },
    [
      collectionId,
      datasetDraft,
      datasetModalMode,
      datasetId,
      fetchDatasetsList,
      handleDatasetModalClose,
      notification,
      setCurrentDataset,
    ],
  );

  const handleDatasetDeleteConfirm = useCallback(async () => {
    if (!collectionId || !datasetDeleteTarget) return;

    try {
      setIsDatasetDeleting(true);
      const result = await deleteDataset(collectionId, datasetDeleteTarget.id);
      if (result && (result as { detail?: string }).detail) {
        notification.error(
          (result as { detail?: string })?.detail || 'Failed to delete dataset',
        );
        return;
      }

      notification.success('Dataset deleted successfully');
      setDatasetDeleteTarget(null);
      await fetchDatasetsList();
      if (datasetDetail && datasetDeleteTarget.id === datasetDetail.id) {
        setDatasetDetail(null);
        setCurrentDataset(null);
        const targetPath = buildPathWithSearch(datasetsPath);
        if (currentPathWithSearch !== targetPath) {
          navigate(targetPath, { replace: true });
        }
      }
    } catch (error) {
      console.error('Dataset delete error:', error);
      notification.error('Failed to delete dataset');
    } finally {
      setIsDatasetDeleting(false);
    }
  }, [
    collectionId,
    datasetDeleteTarget,
    datasetDetail,
    buildPathWithSearch,
    currentPathWithSearch,
    datasetsPath,
    fetchDatasetsList,
    navigate,
    notification,
    setCurrentDataset,
  ]);

  // Handle collection update from modal: write the fresh record into the cache
  // (store sync follows via the collection effect).
  const handleCollectionUpdated = useCallback(
    (updatedCollection: Collection | null) => {
      patchCollectionCache(() => updatedCollection);
    },
    [patchCollectionCache],
  );

  // Handle collection search
  const handleCollectionSearch = useCallback(
    async (filters: FiltersType) => {
      if (!collectionId) return;

      setSearchLoading(true);
      setSearchError('');

      try {
        const customFilters = filters.selectedSites?.map((domain) => ({
          key: 'wp_site',
          value: domain,
        }));

        const response = await searchCollection({
          collectionId,
          query: filters.query,
          dateRange: filters.dateRange,
          scoreThreshold: filters.scoreThreshold,
          expandQuery: filters.expandQuery,
          enableRecencyBoost: filters.enableRecencyBoost,
          topK: filters.topK,
          customFilters,
        });

        if (response.detail) {
          setSearchError(response.detail);
          setSearchResults([]);
          setShowSearchResults(true);
        } else {
          setSearchResults(response.results);
          setShowSearchResults(true);
        }
      } catch (error) {
        console.error('Error searching collection:', error);
        setSearchError('Failed to search collection');
        setSearchResults([]);
        setShowSearchResults(true);
      } finally {
        setSearchLoading(false);
      }
    },
    [collectionId],
  );

  // Handle file deletion
  const handleDeleteSource = async () => {
    if (!selectedSource || !collectionId) return;

    const filenameToDelete = selectedSource.filename;

    try {
      setIsDeleteLoading(true);
      const response = await deleteSource({
        collectionId,
        filename: filenameToDelete,
      });

      if (response?.detail) {
        notification.error(response.detail);
        return;
      }

      notification.success('Source was removed successfully');

      // Remove from local state
      setSources((prev) =>
        prev.filter((source) => source.filename !== filenameToDelete),
      );
      setSelectedFiles((prev) => {
        if (!prev.has(filenameToDelete)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(filenameToDelete);
        return next;
      });

      // Refresh chunk count after deletion
      fetchCollectionInfo();
    } catch (error) {
      console.error('Error deleting source:', error);
      notification.error('Failed to delete file');
    } finally {
      setIsDeleteLoading(false);
      setIsDeleteModalOpen(false);
      setSelectedSource(null);
    }
  };

  const handleBulkDeleteConfirm = useCallback(async () => {
    if (!collectionId || selectedFileNames.length === 0) return;

    const filenames = [...selectedFileNames];

    try {
      setIsBulkDeleteLoading(true);

      const results = await Promise.allSettled(
        filenames.map(async (filename) => {
          const response = await deleteSource({
            collectionId,
            filename,
          });

          if (response?.detail) {
            throw new Error(response.detail);
          }

          return filename;
        }),
      );

      const successful: string[] = [];
      const failures: string[] = [];

      results.forEach((result, index) => {
        const filename = filenames[index];
        if (result.status === 'fulfilled') {
          successful.push(result.value);
        } else {
          const reason =
            result.reason instanceof Error
              ? result.reason.message
              : 'Failed to delete file';
          failures.push(`${filename}: ${reason}`);
        }
      });

      if (successful.length) {
        setSources((prev) =>
          prev.filter((source) => !successful.includes(source.filename)),
        );
        setSelectedFiles((prev) => {
          const next = new Set(prev);
          successful.forEach((name) => {
            next.delete(name);
          });
          return next;
        });
        notification.success(
          successful.length === 1
            ? `Deleted ${successful[0]}`
            : `Deleted ${successful.length} files successfully`,
        );
      }

      if (failures.length) {
        const detail =
          failures.length === 1
            ? failures[0]
            : `Failed to delete ${failures.length} files. ${failures
                .slice(0, 3)
                .join('; ')}`;
        notification.error(detail);
      }
    } catch (error) {
      console.error('Error deleting selected files:', error);
      notification.error('Failed to delete selected files');
    } finally {
      setIsBulkDeleteLoading(false);
      setIsBulkDeleteModalOpen(false);
    }
  }, [collectionId, notification, selectedFileNames, setSelectedFiles]);

  const handleBulkDeleteModalDelete = useCallback(() => {
    if (!hasSelection) {
      setIsBulkDeleteModalOpen(false);
      return;
    }

    return handleBulkDeleteConfirm();
  }, [handleBulkDeleteConfirm, hasSelection]);

  const handleBulkDownload = useCallback(async () => {
    if (!collectionId || selectedFileNames.length === 0) return;

    const orgId = collection?.orgid || currentOrg?.orgid;
    if (!orgId) {
      notification.error('Organization ID is missing');
      return;
    }

    setIsBulkDownloading(true);
    const successes: string[] = [];
    const failures: string[] = [];

    try {
      for (const filename of selectedFileNames) {
        try {
          const result = await getPresignedDownloadUrl({
            orgId,
            collectionId,
            filename,
          });

          if (!result || 'detail' in result) {
            throw new Error(
              ((result as { detail?: string })?.detail ??
                'Failed to generate download link') as string,
            );
          }

          window.open(result.presigned_url, '_blank', 'noopener');

          successes.push(filename);
        } catch (error) {
          const message =
            error instanceof Error ? error.message : 'Failed to download file';
          failures.push(`${filename}: ${message}`);
        }
      }
    } finally {
      setIsBulkDownloading(false);
    }

    if (successes.length) {
      notification.success(
        successes.length === 1
          ? `Started download for ${successes[0]}`
          : `Started downloads for ${successes.length} files`,
      );
    }

    if (failures.length) {
      const detail =
        failures.length === 1
          ? failures[0]
          : `Failed to download ${failures.length} files. ${failures
              .slice(0, 3)
              .join('; ')}`;
      notification.error(detail);
    }
  }, [
    collection?.orgid,
    collectionId,
    currentOrg?.orgid,
    notification,
    selectedFileNames,
  ]);

  // Handle collection deletion
  const handleDeleteCollection = async () => {
    if (!collection || !collectionId) return;

    try {
      setIsCollectionDeleteLoading(true);
      const response = await deleteCollection(collection.id);

      if (response?.detail) {
        notification.error(response.detail);
        return;
      }

      notification.success('Collection scheduled for deletion successfully');

      // Update the cached collection immediately
      patchCollectionCache((prev) =>
        prev
          ? {
              ...prev,
              status: 'pending_deletion',
              deletion_requested_at: new Date().toISOString(),
            }
          : null,
      );
    } catch (error) {
      console.error('Error deleting collection:', error);
      notification.error('Failed to delete collection');
    } finally {
      setIsCollectionDeleteLoading(false);
      setIsCollectionDeleteModalOpen(false);
    }
  };

  const handleHardDeleteCollection = async () => {
    if (!collection || !collectionId) return;

    try {
      setIsCollectionDeleteLoading(true);
      const response = await hardDeleteCollection(collection.id);

      if (response?.detail) {
        notification.error(response.detail);
        setIsCollectionDeleteLoading(false);
        setIsCollectionDeleteModalOpen(false);
        return;
      }

      notification.success('Collection deleted immediately');

      // Navigate back to data sources list
      navigate(ROUTES.DATA_SOURCES.ROOT);

      // Don't clean up state - component will unmount during navigation
      // This prevents race condition where useEffect hooks fire before unmount
    } catch (error) {
      console.error('Error hard deleting collection:', error);
      notification.error('Failed to immediately delete collection');
      // Only clean up on error (when we stay on the page)
      setIsCollectionDeleteLoading(false);
      setIsCollectionDeleteModalOpen(false);
    }
  };

  // Handle cancel collection deletion
  const handleCancelCollectionDeletion = async () => {
    if (!collection || !collectionId) return;

    try {
      setIsCancelLoading(true);
      const response = await cancelCollectionDeletion({
        collectionId: collection.id,
      });

      if (response?.detail) {
        notification.error(response.detail);
        return;
      }

      notification.success('Collection deletion cancelled successfully');

      // Update the cached collection immediately
      patchCollectionCache((prev) =>
        prev
          ? {
              ...prev,
              status: 'active',
              deletion_requested_at: '',
            }
          : null,
      );
    } catch (error) {
      console.error('Error cancelling collection deletion:', error);
      notification.error('Failed to cancel collection deletion');
    } finally {
      setIsCancelLoading(false);
    }
  };

  // Utility functions for table columns
  const readableFileSize = useCallback((mb: number): string => {
    if (mb == null || isNaN(mb)) return '-';
    const bytes = mb * 1024 * 1024;
    const thresh = 1024;
    if (bytes < thresh) return bytes + ' B';
    const units = ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    let u = -1;
    let value = bytes;
    do {
      value /= thresh;
      ++u;
    } while (value >= thresh && u < units.length - 1);
    return value.toFixed(1) + ' ' + units[u];
  }, []);

  const renderSortIcon = useCallback(
    (currentOrderBy: string, asc: string, desc: string) => {
      if (currentOrderBy === asc) return <ChevronUp className='size-4' />;
      if (currentOrderBy === desc) return <ChevronDown className='size-4' />;
      return <ChevronsUpDown className='size-4' />;
    },
    [],
  );

  // Table columns with dropdown menu
  const handleDownload = useCallback(
    async (source: Source) => {
      if (!collectionId) return;

      try {
        const orgId = collection?.orgid || currentOrg?.orgid;

        if (!orgId) {
          notification.error('Organization ID is missing');
          return;
        }

        const result = await getPresignedDownloadUrl({
          orgId,
          collectionId,
          filename: source.filename,
        });

        if (!result || 'detail' in result) {
          notification.error(
            (result as { detail?: string })?.detail ||
              'Failed to generate download link',
          );
          return;
        }

        window.open(result.presigned_url, '_blank', 'noopener');
      } catch (error) {
        console.error('Download error:', error);
        notification.error('Failed to download file');
      }
    },
    [collection?.orgid, collectionId, currentOrg?.orgid, notification],
  );

  const [datasetDetailRows, setDatasetDetailRows] = useState<DatasetFileRow[]>(
    [],
  );

  const datasetKeyByFilename = useMemo(() => {
    const map = new Map<string, string>();
    datasetDetail?.csv_object_keys.forEach((reference) => {
      if (!reference?.s3_key) return;
      const filename = reference.s3_key.split('/').pop();
      if (filename) {
        map.set(filename, reference.s3_key);
      }
    });
    return map;
  }, [datasetDetail?.csv_object_keys]);

  const datasetQualityLookup = useMemo(() => {
    const byKey = new Map<string, CSVQuality>();
    const byFilename = new Map<string, CSVQuality>();

    datasetMetadataTables.forEach((table) => {
      if (!table?.quality) return;
      const quality = table.quality;
      const potentialKeys = [
        table.s3_key,
        table.source_key,
        table.csv_key,
      ].filter((value): value is string => Boolean(value));

      potentialKeys.forEach((key) => {
        byKey.set(key, quality);
        const name = key.split('/').pop();
        if (name) {
          byFilename.set(name, quality);
        }
      });

      if (table.filename) {
        byFilename.set(table.filename, quality);
      }

      if (table.table) {
        byFilename.set(table.table, quality);
      }
    });

    return { byKey, byFilename };
  }, [datasetMetadataTables]);

  const unresolvedQualityTables = useMemo(
    () =>
      datasetMetadataTables.filter(
        (table) => table.quality?.requires_resolution === true,
      ),
    [datasetMetadataTables],
  );

  const resolveFocusKey = useCallback(
    (table: DatasetTableMetadata) => {
      return (
        table.s3_key ||
        table.source_key ||
        table.csv_key ||
        (table.filename ? datasetKeyByFilename.get(table.filename) : undefined)
      );
    },
    [datasetKeyByFilename],
  );

  const datasetDetailColumns = useMemo<ColumnDef<DatasetFileRow>[]>(
    () => [
      {
        accessorKey: 'filename',
        header: 'Filename',
        cell: ({ row }) => {
          const externalId = row.original.tags?.external_id;
          return (
            <div className='flex flex-col gap-0.5 min-w-0'>
              <span className='font-semibold text-foreground truncate'>
                {row.original.filename}
              </span>
              {externalId && (
                <div className='flex items-center gap-1 text-xs text-muted-foreground'>
                  <span className='truncate max-w-[260px] font-mono'>
                    {externalId}
                  </span>
                  <CopyButton
                    text={externalId}
                    size='small'
                    ariaLabel='Copy external ID'
                  />
                </div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'quality_status',
        header: 'Data quality',
        cell: ({ row }) => {
          const tableMatch = datasetMetadataTables.find((table) => {
            if (table.s3_key && table.s3_key === row.original.s3_key)
              return true;
            if (table.source_key && table.source_key === row.original.s3_key)
              return true;
            if (table.csv_key && table.csv_key === row.original.s3_key)
              return true;
            if (table.filename && table.filename === row.original.filename)
              return true;
            if (table.table && table.table === row.original.filename)
              return true;
            return false;
          });

          const quality =
            tableMatch?.quality ||
            (row.original.s3_key &&
              datasetQualityLookup.byKey.get(row.original.s3_key)) ||
            datasetQualityLookup.byFilename.get(row.original.filename);

          if (!quality) {
            return datasetMetadataLoading ? (
              <span className='text-xs text-muted-foreground'>Loading…</span>
            ) : (
              <span className='text-xs text-muted-foreground'>—</span>
            );
          }

          const requiresResolution = quality.requires_resolution;
          const focusKey =
            row.original.s3_key ||
            (tableMatch ? resolveFocusKey(tableMatch) : undefined) ||
            datasetKeyByFilename.get(row.original.filename);

          const handleResolveClick = (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            if (datasetDetail) {
              if (focusKey) {
                handleEditDataset(datasetDetail, { focusKey });
              } else {
                notification.warning(
                  'Unable to locate this file in the dataset editor.',
                );
                handleEditDataset(datasetDetail);
              }
            }
          };

          return (
            <div className='flex flex-col items-start gap-1'>
              <Badge
                variant='secondary'
                className={cn(
                  'px-2 py-1 text-[11px] font-medium',
                  requiresResolution
                    ? 'bg-red-100 text-red-700'
                    : 'bg-green-100 text-green-700',
                )}
              >
                {requiresResolution ? 'Action needed' : 'Resolved'}
              </Badge>
              {canEdit && requiresResolution && (
                <button
                  type='button'
                  onClick={handleResolveClick}
                  className='text-xs text-primary hover:underline'
                >
                  Resolve issues
                </button>
              )}
            </div>
          );
        },
      },
      {
        id: 'preview',
        header: 'Preview',
        cell: ({ row }) => (
          <Button
            type='button'
            variant='ghost'
            size='icon'
            aria-label={`Preview ${row.original.filename}`}
            onClick={(event) => {
              event.stopPropagation();
              setPreviewCsv({
                key: row.original.s3_key,
                filename: row.original.filename,
              });
            }}
          >
            <TableIcon className='size-4' />
          </Button>
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'size_mb',
        header: 'Size',
        cell: ({ row }) =>
          row.original.size_mb == null || isNaN(row.original.size_mb) ? (
            <span className='text-muted-foreground'>-</span>
          ) : (
            <span>{readableFileSize(row.original.size_mb)}</span>
          ),
      },
      {
        accessorKey: 'upload_timestamp',
        header: 'Uploaded',
        cell: ({ row }) => {
          const uploaded = row.original.upload_timestamp;
          if (!uploaded)
            return <span className='text-muted-foreground'>-</span>;
          return <span>{format(new Date(uploaded), 'MM/dd/yyyy')}</span>;
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className='flex justify-end'>
            <Button
              variant='ghost'
              size='icon'
              onClick={() => handleDownload(row.original)}
              aria-label={`Download ${row.original.filename}`}
            >
              <Download className='size-4' />
            </Button>
          </div>
        ),
        enableSorting: false,
      },
    ],
    [
      datasetDetail,
      datasetKeyByFilename,
      datasetMetadataLoading,
      datasetMetadataTables,
      datasetQualityLookup,
      handleDownload,
      handleEditDataset,
      notification,
      resolveFocusKey,
      readableFileSize,
    ],
  );

  const datasetMetadata = useMemo(() => {
    if (!datasetDetail) {
      return {
        csvCount: datasetDetailLoading ? 'Loading…' : '—',
        created: datasetDetailLoading ? 'Loading…' : '—',
        updated: datasetDetailLoading ? 'Loading…' : '—',
      };
    }

    return {
      csvCount: datasetDetail.csv_object_keys.length.toString(),
      created: format(new Date(datasetDetail.created_at), 'MM/dd/yyyy'),
      updated: format(new Date(datasetDetail.updated_at), 'MM/dd/yyyy'),
    };
  }, [datasetDetail, datasetDetailLoading]);

  const columns: ColumnDef<Source>[] = useMemo(() => {
    const selectionColumn: ColumnDef<Source> = {
      id: 'selection',
      header: () => (
        <div className='flex justify-center'>
          <Checkbox
            aria-label='Select all files'
            checked={headerCheckboxState}
            onCheckedChange={(checked) =>
              toggleSelectAllVisible(checked === true)
            }
            className='translate-y-[1px]'
          />
        </div>
      ),
      cell: ({ row }) => (
        <div className='flex justify-center'>
          <Checkbox
            aria-label={`Select ${row.original.filename}`}
            checked={selectedFiles.has(row.original.filename)}
            onCheckedChange={(checked) =>
              toggleFileSelection(row.original.filename, checked === true)
            }
          />
        </div>
      ),
      enableSorting: false,
      meta: { className: 'w-12' },
    };

    const baseColumns: ColumnDef<Source>[] = [
      selectionColumn,
      {
        accessorKey: 'filename',
        header: () => (
          <button
            type='button'
            className='flex items-center gap-1'
            onClick={() => handleSort('filename')}
          >
            Name
            {renderSortIcon(
              orderBy,
              SourceOrder.NAME_ASC,
              SourceOrder.NAME_DESC,
            )}
          </button>
        ),
        cell: ({ row }) => {
          const externalId = row.original.tags?.external_id;
          return (
            <div className='flex flex-col gap-0.5 min-w-0'>
              <span className='font-semibold text-foreground truncate'>
                {row.original.filename}
              </span>
              {externalId && (
                <div className='flex items-center gap-1 text-xs text-muted-foreground'>
                  <span className='truncate max-w-[260px] font-mono'>
                    {externalId}
                  </span>
                  <CopyButton
                    text={externalId}
                    size='small'
                    ariaLabel='Copy external ID'
                  />
                </div>
              )}
            </div>
          );
        },
      },
    ];

    // Only show ingestion status for unstructured collections (they go through embedding/ingestion pipeline)
    if (collection?.data_type !== 'structured') {
      baseColumns.push({
        accessorKey: 'ingestion_status',
        header: 'Ingestion Status',
        cell: ({ row }) => {
          const status = getDisplayStatus(
            row.original.tags?.indexing_status,
            row.original.tags?.ingestion_method,
          );
          return <IngestionStatusBadge status={status} />;
        },
        enableSorting: false,
      });
    }

    // if (!isStructuredCollection) {
    //   baseColumns.push({
    //     accessorKey: 'indexing_status',
    //     header: 'Indexing status',
    //     cell: ({ row }) => {
    //       const status = row.original.tags?.indexing_status || 'unknown';
    //       const statusColors = {
    //         completed: 'bg-green-100 text-green-800',
    //         processing: 'bg-yellow-100 text-yellow-800',
    //         failed: 'bg-red-100 text-red-800',
    //         unknown: 'bg-gray-100 text-gray-800',
    //       };

    //       return (
    //         <span
    //           className={`px-2 py-1 rounded-full text-xs font-medium ${
    //             statusColors[status as keyof typeof statusColors] ||
    //             statusColors.unknown
    //           }`}
    //         >
    //           {status.charAt(0).toUpperCase() + status.slice(1)}
    //         </span>
    //       );
    //     },
    //   });
    // }

    baseColumns.push(
      {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ row }) => {
          const typeLabel = getFileTypeLabel(row.original.content_type);
          return <span>{typeLabel || '-'}</span>;
        },
      },
      {
        accessorKey: 'size',
        header: () => (
          <button
            type='button'
            className='flex items-center gap-1'
            onClick={() => handleSort('size')}
          >
            Size
            {renderSortIcon(
              orderBy,
              SourceOrder.SIZE_ASC,
              SourceOrder.SIZE_DESC,
            )}
          </button>
        ),
        cell: ({ row }) => (
          <span>{readableFileSize(row.original.size_mb ?? 0)}</span>
        ),
      },
      {
        accessorKey: 'last_modified',
        header: () => (
          <button
            type='button'
            className='flex items-center gap-1'
            onClick={() => handleSort('last_modified')}
          >
            Last Updated
            {renderSortIcon(
              orderBy,
              SourceOrder.DATE_ASC,
              SourceOrder.DATE_DESC,
            )}
          </button>
        ),
        cell: ({ row }) => {
          const lastModified = row.original.last_modified;
          if (!lastModified) {
            return <span className='text-sm text-gray-500'>-</span>;
          }

          return <span>{format(new Date(lastModified), 'MM/dd/yyyy')}</span>;
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const canPreview =
            collection?.data_type !== 'structured' &&
            isPreviewableMimeType(row.original.content_type);

          return (
            <div className='flex justify-end items-center gap-1'>
              {canPreview && (
                <Button
                  variant='ghost'
                  size='icon'
                  onClick={() =>
                    setFilePreview({
                      filename: row.original.filename,
                      contentType: row.original.content_type,
                    })
                  }
                  aria-label={`Preview ${row.original.filename}`}
                >
                  <Eye className='size-4' />
                </Button>
              )}
              <Button
                variant='ghost'
                size='icon'
                onClick={() => handleDownload(row.original)}
                aria-label={`Download ${row.original.filename}`}
              >
                <Download className='size-4' />
              </Button>
              {collection?.data_type !== 'structured' && (
                <Button
                  variant='ghost'
                  size='icon'
                  onClick={() => {
                    setSelectedSource(row.original);
                    setIsFileInfoModalOpen(true);
                  }}
                  aria-label={`Info for ${row.original.filename}`}
                >
                  <Info className='size-4' />
                </Button>
              )}
              {(canDelete || canEditFileFields) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant='ghost' size='icon'>
                      <MoreVertical />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end'>
                    {canEditFileFields && (
                      <>
                        <DropdownMenuItem
                          disabled={!row.original.tags?.external_id}
                          onClick={() => {
                            setSelectedSource(row.original);
                            setIsEditFileFieldsModalOpen(true);
                          }}
                        >
                          <Pencil className='size-4 mr-2' />
                          Edit fields
                        </DropdownMenuItem>
                        {canDelete && <DropdownMenuSeparator />}
                      </>
                    )}
                    {canDelete && (
                      <DropdownMenuItem
                        variant='destructive'
                        onClick={() => {
                          setSelectedSource(row.original);
                          setIsDeleteModalOpen(true);
                        }}
                      >
                        <Trash className='size-4 mr-2' />
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          );
        },
        enableSorting: false,
        size: 60,
      },
    );

    return baseColumns;
  }, [
    canDelete,
    canEdit,
    canEditFileFields,
    collection,
    getDisplayStatus,
    handleDownload,
    handleSort,
    headerCheckboxState,
    orderBy,
    readableFileSize,
    renderSortIcon,
    selectedFiles,
    toggleFileSelection,
    toggleSelectAllVisible,
  ]);

  if (!collectionId) {
    return (
      <div className='flex items-center justify-center h-64'>
        <p className='text-gray-500'>Collection not found</p>
      </div>
    );
  }

  // Show skeleton while loading
  if (collectionLoading) {
    return <CollectionSkeleton />;
  }

  const deletionDate = new Date(collection?.deletion_requested_at || '');
  const ttlMilliseconds = getTtlInMilliseconds(ttlValue, ttlUnit);
  const expirationDate = new Date(deletionDate.getTime() + ttlMilliseconds);
  const now = new Date();

  // Calculate time left based on the unit
  const timeLeftInMilliseconds = expirationDate.getTime() - now.getTime();
  const timeLeft = Math.ceil(
    timeLeftInMilliseconds / getTtlInMilliseconds(1, ttlUnit),
  );

  const isDatasetDetailRoute = Boolean(datasetId);

  return (
    <div className='w-full space-y-6'>
      {/* Header */}
      <div className='space-y-4'>
        {isDatasetDetailRoute ? (
          <div className='space-y-3' id='data-quality-summary'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => {
                const targetPath = buildPathWithSearch(datasetsPath);
                if (currentPathWithSearch !== targetPath) {
                  navigate(targetPath);
                }
              }}
            >
              Back to datasets
            </Button>
            <div className='flex items-start justify-between gap-4'>
              <div className='flex flex-col gap-2'>
                <h1 className='text-2xl font-bold'>
                  {datasetDetail?.name ||
                    (datasetDetailLoading ? 'Loading…' : 'Dataset')}
                </h1>
                {(datasetDetail?.description || datasetDetailLoading) && (
                  <p className='text-gray-600 whitespace-pre-wrap'>
                    {datasetDetail?.description || ' '}
                  </p>
                )}
                <div className='flex flex-wrap gap-4 text-sm text-muted-foreground'>
                  <span>CSV files: {datasetMetadata.csvCount}</span>
                  <span>Created: {datasetMetadata.created}</span>
                  <span>Updated: {datasetMetadata.updated}</span>
                </div>
              </div>
              {canEdit && (
                <div className='flex items-center gap-2'>
                  <Button
                    variant='outline'
                    disabled={!datasetDetail || datasetDetailLoading}
                    onClick={() =>
                      datasetDetail && handleEditDataset(datasetDetail)
                    }
                  >
                    Edit dataset
                  </Button>
                  <Button
                    variant='destructive'
                    disabled={!datasetDetail || datasetDetailLoading}
                    onClick={() =>
                      datasetDetail && handleDatasetDeleteRequest(datasetDetail)
                    }
                  >
                    Delete
                  </Button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className='flex items-start gap-2'>
              <div className='flex gap-2 flex-col'>
                <div className='flex items-center gap-2'>
                  <h1 className='text-2xl font-bold'>{collection?.name}</h1>
                  {canEdit && (
                    <Button
                      variant='ghost'
                      size='icon'
                      onClick={() => setIsEditModalOpen(true)}
                      disabled={loading || collectionLoading}
                    >
                      <Edit2 />
                    </Button>
                  )}
                  {canDelete && collection?.status !== 'pending_deletion' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant='ghost'
                          size='icon'
                          onClick={() => setIsCollectionDeleteModalOpen(true)}
                          disabled={loading || collectionLoading}
                          aria-label='Delete collection'
                        >
                          <Trash className='size-4 text-destructive' />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Delete collection</TooltipContent>
                    </Tooltip>
                  )}
                  {canDelete && collection?.status === 'pending_deletion' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant='outline'
                          size='icon'
                          onClick={() => handleCancelCollectionDeletion()}
                          disabled={isCancelLoading}
                          aria-label='Cancel deletion'
                        >
                          <RotateCcw className='size-4' />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Cancel deletion</TooltipContent>
                    </Tooltip>
                  )}
                </div>
                {collection?.data_type && (
                  <div
                    id='collection-page-data-type-indicator'
                    className='inline-flex items-center gap-1.5 self-start rounded-full border px-2.5 py-0.5 text-xs font-medium text-muted-foreground'
                  >
                    {collection.data_type === 'structured' ? (
                      <>
                        <TableIcon className='h-3.5 w-3.5' />
                        <span>Structured Data</span>
                      </>
                    ) : (
                      <>
                        <Database className='h-3.5 w-3.5' />
                        <span>Unstructured Data</span>
                      </>
                    )}
                  </div>
                )}
                {isAdmin && (
                  <div className='flex items-center gap-1 text-xs text-muted-foreground'>
                    <span className='truncate max-w-[260px]'>
                      {collection?.id || '—'}
                    </span>
                    <CopyButton
                      text={collection?.id || ''}
                      size='small'
                      aria-label='Copy collection ID'
                    />
                  </div>
                )}
                {collection?.status === 'pending_deletion' && (
                  <div className='flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400'>
                    <Hourglass className='size-3' />
                    <div>
                      Deletion requested:{' '}
                      {format(new Date(deletionDate), 'MM/dd/yyyy')}.{' '}
                      {timeLeft > 0
                        ? `${timeLeft} ${formatTimeUnit(timeLeft, ttlUnit)} remaining`
                        : 'Deletion overdue'}
                    </div>
                  </div>
                )}
                {isUnstructuredQdrantCollection && (
                  <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                    <span className='font-medium text-foreground'>
                      Text chunks indexed:
                    </span>
                    <span>{formattedPointsCount}</span>
                    <button
                      onClick={fetchCollectionInfo}
                      disabled={collectionInfoLoading}
                      className='text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                      aria-label='Refresh chunk count'
                      title='Refresh chunk count'
                    >
                      <RefreshCw
                        className={`h-3.5 w-3.5 ${collectionInfoLoading ? 'animate-spin' : ''}`}
                      />
                    </button>
                    <HelpTooltip label='Text chunk count help'>
                      Total number of vector points (text chunks) ingested for
                      this collection.
                    </HelpTooltip>
                    <button
                      onClick={() => setIsCollectionInfoModalOpen(true)}
                      disabled={collectionInfoLoading || !collectionInfo}
                      className='ml-0.5 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                      aria-label='View collection info JSON'
                      title='View collection info'
                    >
                      <Info className='size-4' />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            {collection?.description && (
              <p className='text-gray-600 whitespace-pre-wrap'>
                {collection.description}
              </p>
            )}

            <div className='flex flex-wrap gap-4 text-sm text-muted-foreground'>
              <div>
                <span className='font-medium text-foreground'>Source:</span>{' '}
                {getCollectionSourceLabel(collectionSource)}
              </div>
              {collection?.data_type === 'structured' && (
                <div>
                  <span className='font-medium text-foreground'>
                    Destination:
                  </span>{' '}
                  Analytics
                </div>
              )}
              {isWordPressCollection && (
                <div className='w-full'>
                  <span className='font-medium text-foreground'>Sites:</span>{' '}
                  {wordPressSites.length} configured
                  <span className='text-muted-foreground'>
                    {' '}
                    · Configure in the WordPress Settings tab
                  </span>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Structured-collection "how it works" guide — customer-facing,
          collapsed by default, explains what to expect without exposing
          implementation details. */}
      {!isDatasetDetailRoute && collection?.data_type === 'structured' && (
        <StructuredUploadGuide />
      )}

      {/* Top-level tabs: Files, Search, Chat, External Chat */}
      {!isDatasetDetailRoute && (
        <Tabs
          value={mainTab}
          onValueChange={handleMainTabChange}
          className='space-y-4'
        >
          <div className='flex items-center justify-between'>
            <TabsList>
              {isWordPressCollection && (
                <TabsTrigger value='wordpress-settings'>
                  <Settings className='size-4' />
                  WordPress Settings
                </TabsTrigger>
              )}
              <TabsTrigger value='files'>
                <CloudUpload className='size-4' />
                Files
              </TabsTrigger>
              {isUnstructuredQdrantCollection && (
                <TabsTrigger value='records'>
                  <Layers className='size-4' />
                  Records
                </TabsTrigger>
              )}
              {collection?.data_type !== 'structured' && (
                <TabsTrigger value='search'>
                  <Search className='size-4' />
                  Search
                </TabsTrigger>
              )}
              {isUnstructuredQdrantCollection && isCollectionChatEnabled && (
                <TabsTrigger value='chat'>
                  <BotMessageSquare className='size-4' />
                  Chat
                </TabsTrigger>
              )}
              {isUnstructuredQdrantCollection && isExternalChatEnabled && (
                <TabsTrigger value='external-chat'>
                  <MessageSquareShare className='size-4' />
                  External Chat
                  <PreviewBadge />
                </TabsTrigger>
              )}
            </TabsList>
            {canShare && (
              <Button
                variant='outline'
                onClick={() => setIsShareModalOpen(true)}
              >
                <Share2 className='size-4' />
                Share
              </Button>
            )}
          </div>

          {/* WordPress Settings tab — all WP source configuration lives here
              so the primary nav stays above the fold on large multi-site
              collections. Editable inline cards when the user can edit;
              read-only panels otherwise. */}
          {isWordPressCollection && (
            <TabsContent value='wordpress-settings'>
              <div className='space-y-4'>
                <div className='flex items-start gap-3 rounded-lg border border-border/60 bg-muted/40 px-4 py-3'>
                  <Settings className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
                  <p className='text-sm leading-relaxed text-muted-foreground'>
                    Manage the WordPress sites that feed this collection, their
                    credentials and schedules, and nightly deletion reconcile.
                  </p>
                </div>

                {canEdit ? (
                  <WpSiteInlineEditor
                    sites={wordPressSites}
                    sourceConfig={sourceConfig}
                    reconcileEnabled={sourceConfig?.reconcile_enabled ?? false}
                    onSaveSite={handleSaveSiteConfig}
                    onRemoveSite={handleRequestRemoveSite}
                    renderSummaryExtra={(site) => (
                      <>
                        {' · '}
                        <WpSiteLastIndexed
                          baseUrl={site.baseUrl}
                          sitesLastIndexed={sitesLastIndexed}
                          variant='multi'
                        />
                      </>
                    )}
                    renderBodyExtra={(site) => {
                      const siteRuleCount = site.exclusionRules.length;
                      return (
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          onClick={() => {
                            setWpExclusionSiteId(site.siteId);
                            setIsWpExclusionModalOpen(true);
                          }}
                        >
                          <Filter className='size-4' />
                          {siteRuleCount > 0
                            ? `${siteRuleCount} content ${siteRuleCount === 1 ? 'filter' : 'filters'}`
                            : 'Configure content filters'}
                        </Button>
                      );
                    }}
                  />
                ) : (
                  <div className='space-y-2'>
                    {wordPressSites.map((site) => {
                      const siteRuleCount = site.exclusionRules.length;
                      return (
                        <div
                          key={site.siteId}
                          className='rounded-md border border-border bg-muted/30 px-4 py-3 text-sm'
                        >
                          <div className='flex items-center gap-2'>
                            <span className='font-medium text-foreground'>
                              {site.label || site.baseUrl}
                            </span>
                            {site.label && site.baseUrl && (
                              <span className='text-muted-foreground truncate max-w-[260px]'>
                                {site.baseUrl}
                              </span>
                            )}
                          </div>
                          <div className='mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-muted-foreground'>
                            <span>
                              Cadence: {site.scheduleCron || '—'}
                              {!site.cronEnabled && ' (paused)'}
                            </span>
                            <span>
                              Auth:{' '}
                              {site.useAuth
                                ? site.hasCredentials
                                  ? 'SSM'
                                  : site.username || '—'
                                : 'None'}
                            </span>
                            {site.postTypes.length > 0 && (
                              <span>Types: {site.postTypes.join(', ')}</span>
                            )}
                            <WpSiteLastIndexed
                              baseUrl={site.baseUrl}
                              sitesLastIndexed={sitesLastIndexed}
                              variant='multi'
                            />
                          </div>
                          {siteRuleCount > 0 && (
                            <div className='mt-1 text-muted-foreground'>
                              {siteRuleCount} content{' '}
                              {siteRuleCount === 1 ? 'filter' : 'filters'}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>
          )}

          {/* Files tab */}
          <TabsContent value='files'>
            <div className='space-y-4'>
              {/* Upload API docs — collapsible at the top of Files (moved out
                  of the primary nav). Opens by default on ?upload-api=1. */}
              {isUnstructuredQdrantCollection &&
                isUploadApiEnabled &&
                collectionId && (
                  <Collapsible
                    open={uploadApiOpen}
                    onOpenChange={setUploadApiOpen}
                  >
                    <div className='rounded-lg border border-border'>
                      <CollapsibleTrigger asChild>
                        <button
                          type='button'
                          className='flex w-full items-center gap-2 p-3 text-left'
                        >
                          <ChevronDown
                            className={cn(
                              'size-4 shrink-0 transition-transform duration-200',
                              !uploadApiOpen && '-rotate-90',
                            )}
                          />
                          <FileCode className='size-4 shrink-0 text-muted-foreground' />
                          <span className='text-sm font-medium'>
                            Upload API
                          </span>
                          <PreviewBadge />
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className='border-t border-border p-4'>
                          <UploadApiTab
                            orgId={currentOrg?.orgid || ''}
                            collectionId={collectionId}
                            exposedAttributes={
                              collection?.exposed_upload_attributes ?? []
                            }
                          />
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                )}
              <div className='flex items-start gap-3 rounded-lg border border-border/60 bg-muted/40 px-4 py-3'>
                <CloudUpload className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
                <p className='text-sm leading-relaxed text-muted-foreground'>
                  Upload and manage the source documents that feed this
                  collection. Files are processed and indexed automatically
                  after upload.
                </p>
              </div>
              <div className='flex items-center gap-2 mb-4'>
                <div className='flex items-center gap-2 w-96'>
                  <SearchInput
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder={
                      activeTab === 'datasets'
                        ? 'Search datasets by name or description...'
                        : 'Search by file name...'
                    }
                    aria-label={
                      activeTab === 'datasets'
                        ? 'Search datasets by name or description'
                        : 'Search data collections by file name'
                    }
                  />
                </div>
                {canEdit && (
                  <Button onClick={() => setIsNewDataSourceModalOpen(true)}>
                    Upload
                  </Button>
                )}
              </div>

              {isStructuredCollectionsEnabled &&
              collection?.data_type === 'structured' ? (
                <Tabs
                  value={activeTab}
                  onValueChange={handleTabChange}
                  className='space-y-4'
                >
                  <TabsList>
                    <TabsTrigger value='files'>All Files</TabsTrigger>
                    <TabsTrigger value='datasets'>
                      Datasets
                      {datasets.length ? ` (${datasets.length})` : ''}
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value='files'>
                    <div className='space-y-4 mt-2'>
                      <div className='flex items-center justify-between'>
                        <h3 className='text-lg font-medium'>
                          Files ({filteredSources.length})
                        </h3>
                        <div className='flex items-center gap-2'>
                          {hasSelection && (
                            <>
                              <Button
                                variant='outline'
                                disabled={isBulkDownloading || loading}
                                onClick={handleBulkDownload}
                              >
                                <Download />
                                Download selected ({selectedFilesCount})
                              </Button>
                              {canDelete && (
                                <Button
                                  variant='destructive'
                                  disabled={isBulkDeleteLoading || loading}
                                  onClick={() => setIsBulkDeleteModalOpen(true)}
                                >
                                  <Trash />
                                  Delete selected ({selectedFilesCount})
                                </Button>
                              )}
                            </>
                          )}

                          <Button
                            onClick={() => fetchSources(true)}
                            variant='ghost'
                            size='icon'
                            aria-label='Refresh data collections'
                            disabled={refreshing}
                          >
                            <RefreshCw
                              className={cn(
                                'size-4',
                                refreshing && 'animate-spin',
                              )}
                            />
                          </Button>
                        </div>
                      </div>

                      <Table
                        columns={columns}
                        data={filteredSources}
                        loading={loading}
                      />
                    </div>
                  </TabsContent>

                  <TabsContent value='datasets'>
                    <div className='space-y-4 mt-2'>
                      <div className='flex items-center justify-between'>
                        <div className='flex items-center gap-2'>
                          <h3 className='text-lg font-medium'>
                            Datasets ({filteredDatasets.length})
                          </h3>
                          <PreviewBadge />
                        </div>
                        {canEdit && (
                          <Button onClick={handleOpenCreateDataset}>
                            Create dataset
                          </Button>
                        )}
                      </div>

                      <DatasetsTable
                        datasets={filteredDatasets}
                        loading={datasetsLoading}
                        onEdit={handleEditDataset}
                        onDelete={handleDatasetDeleteRequest}
                        onOpen={handleOpenDatasetDetail}
                        canEdit={canEdit}
                        canDelete={canDelete}
                      />
                    </div>
                  </TabsContent>
                </Tabs>
              ) : (
                <div className='space-y-4 mt-2'>
                  <div className='flex items-center justify-between'>
                    <h3 className='text-lg font-medium'>
                      Files ({filteredSources.length})
                    </h3>
                    <div className='flex items-center gap-2'>
                      {hasSelection && (
                        <>
                          <Button
                            variant='outline'
                            disabled={isBulkDownloading || loading}
                            onClick={handleBulkDownload}
                          >
                            <Download />
                            Download selected ({selectedFilesCount})
                          </Button>
                          {canDelete && (
                            <Button
                              variant='destructive'
                              disabled={isBulkDeleteLoading || loading}
                              onClick={() => setIsBulkDeleteModalOpen(true)}
                            >
                              <Trash />
                              Delete selected ({selectedFilesCount})
                            </Button>
                          )}
                        </>
                      )}

                      <Button
                        onClick={() => fetchSources(true)}
                        variant='ghost'
                        size='icon'
                        aria-label='Refresh data collections'
                        disabled={refreshing}
                      >
                        <RefreshCw
                          className={cn('size-4', refreshing && 'animate-spin')}
                        />
                      </Button>
                    </div>
                  </div>

                  <Table
                    columns={columns}
                    data={filteredSources}
                    loading={loading}
                  />
                </div>
              )}
            </div>
          </TabsContent>

          {/* Search tab */}
          {collection?.data_type !== 'structured' && (
            <TabsContent value='search'>
              <div className='space-y-4'>
                <div className='flex items-start gap-3 rounded-lg border border-border/60 bg-muted/40 px-4 py-3'>
                  <Search className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
                  <p className='text-sm leading-relaxed text-muted-foreground'>
                    Spot-check that your content is indexed correctly and
                    fine-tune search behavior by adjusting parameters like
                    result count, score threshold, and search type.
                  </p>
                </div>
                <CollectionSearchFilters
                  onSearch={handleCollectionSearch}
                  loading={searchLoading}
                  siteDomains={
                    wpSiteDomains.length > 1 ? wpSiteDomains : undefined
                  }
                />

                {showSearchResults && (
                  <CollectionSearchResults
                    results={searchResults}
                    loading={searchLoading}
                    error={searchError}
                    onClose={() => setShowSearchResults(false)}
                  />
                )}
              </div>
            </TabsContent>
          )}

          {isUnstructuredQdrantCollection && (
            <TabsContent value='records'>
              <div className='space-y-4'>
                <div className='flex items-start gap-3 rounded-lg border border-border/60 bg-muted/40 px-4 py-3'>
                  <Layers className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
                  <p className='text-sm leading-relaxed text-muted-foreground'>
                    Browse the indexed records in this collection — including
                    documents ingested via the API, WordPress, or captured
                    citations — with their stored metadata.
                  </p>
                </div>
                {collectionId && (
                  <CollectionDocumentsList collectionId={collectionId} />
                )}
              </div>
            </TabsContent>
          )}

          {/* Chat tab */}
          {isUnstructuredQdrantCollection && isCollectionChatEnabled && (
            <TabsContent value='chat'>
              <div className='mb-4 flex items-start gap-3 rounded-lg border border-border/60 bg-muted/40 px-4 py-3'>
                <BotMessageSquare className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
                <p className='text-sm leading-relaxed text-muted-foreground'>
                  Chat with your indexed data to test responses, refine prompts,
                  and explore what the collection knows before exposing it
                  externally.
                </p>
              </div>
              <CollectionChatCard
                orgId={currentOrg?.orgid || null}
                collectionId={collectionId ?? null}
                collectionName={collection?.name || null}
                authToken={authToken}
                onCustomizePrompt={() => setIsChatPromptModalOpen(true)}
                customPromptActive={hasCustomChatPrompt}
                onOpenChatFilters={handleOpenChatFilters}
                isAdmin={isAdmin}
                savedPrompts={collection?.config?.chat_prompts}
                onSavePrompt={handleSavePrompt}
                onDeletePrompt={handleDeletePrompt}
                onReorderPrompts={handleReorderPrompts}
              />
            </TabsContent>
          )}

          {/* External Chat tab */}
          {isUnstructuredQdrantCollection &&
            isExternalChatEnabled &&
            collectionId && (
              <TabsContent value='external-chat'>
                {/* Keyed by collection: the tab holds an unsaved config draft
                    and version-modal state, and its mutations resolve async.
                    Without a remount, a draft or a late activate response from
                    the previous collection lands on this one. */}
                <ExternalChatTab
                  key={collectionId}
                  collectionId={collectionId}
                  collectionName={collection?.name || ''}
                  orgId={currentOrg?.orgid || ''}
                  externalChatFilters={collection?.external_chat_filters}
                  onCollectionUpdated={refetchCollection}
                />
              </TabsContent>
            )}
        </Tabs>
      )}

      {isDatasetDetailRoute && collection?.data_type === 'structured' && (
        <div className='space-y-4 mt-2'>
          {datasetDetail && (
            <LLMPlaygroundCard
              orgId={collection?.orgid || currentOrg?.orgid || ''}
              collectionId={collectionId || ''}
              datasetId={datasetDetail.id}
            />
          )}

          <div className='space-y-3' ref={qualitySectionRef}>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <h3 className='text-lg font-medium'>Data quality</h3>
              {datasetMetadataLoading && (
                <span className='text-xs text-muted-foreground'>Checking…</span>
              )}
              {!qualityShouldLoad && !datasetMetadataLoading && (
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  onClick={() => setQualityShouldLoad(true)}
                >
                  Load data quality
                </Button>
              )}
            </div>
            {datasetMetadataError && (
              <p className='text-sm text-destructive'>{datasetMetadataError}</p>
            )}
            {!qualityShouldLoad ? (
              <p className='text-sm text-muted-foreground'>
                Scroll to this section or select “Load data quality” to fetch
                the latest Glue metadata.
              </p>
            ) : datasetMetadataTables.length === 0 &&
              !datasetMetadataLoading ? (
              <p className='text-sm text-muted-foreground'>
                No data-quality issues detected for this dataset.
              </p>
            ) : (
              <div className='space-y-2'>
                <div className='flex flex-wrap items-center gap-2 text-xs'>
                  <Badge className='bg-red-100 text-red-700'>
                    Action needed: {unresolvedQualityTables.length}
                  </Badge>
                  <Badge className='bg-green-100 text-green-700'>
                    Resolved:{' '}
                    {Math.max(
                      datasetMetadataTables.length -
                        unresolvedQualityTables.length,
                      0,
                    )}
                  </Badge>
                </div>
                <div className='space-y-2'>
                  {datasetMetadataTables.map((table, index) => {
                    const displayName =
                      table.filename ||
                      table.table ||
                      table.s3_key?.split('/').pop() ||
                      table.source_key?.split('/').pop() ||
                      `Table ${index + 1}`;
                    const quality = table.quality;
                    const requiresResolution = quality?.requires_resolution;
                    const focusKey = resolveFocusKey(table);

                    const handleResolve = () => {
                      if (datasetDetail) {
                        if (focusKey) {
                          handleEditDataset(datasetDetail, { focusKey });
                        } else {
                          notification.warning(
                            'Unable to locate this file in the dataset editor.',
                          );
                          handleEditDataset(datasetDetail);
                        }
                      }
                    };

                    return (
                      <div
                        key={`${displayName}-${focusKey ?? index}`}
                        className='flex flex-col gap-3 rounded-md border p-3 md:flex-row md:items-center md:justify-between'
                      >
                        <div className='space-y-1'>
                          <p className='text-sm font-medium'>{displayName}</p>
                          {quality?.issues?.length ? (
                            <p className='text-xs text-muted-foreground'>
                              {quality.issues.length} issue
                              {quality.issues.length === 1 ? '' : 's'} detected
                            </p>
                          ) : (
                            <p className='text-xs text-muted-foreground'>
                              No outstanding issues.
                            </p>
                          )}
                        </div>
                        <div className='flex items-center gap-2'>
                          <Badge
                            className={cn(
                              'px-2 py-1 text-[11px] font-medium',
                              requiresResolution
                                ? 'bg-red-100 text-red-700'
                                : 'bg-green-100 text-green-700',
                            )}
                          >
                            {requiresResolution ? 'Action needed' : 'Resolved'}
                          </Badge>
                          <Button
                            type='button'
                            size='sm'
                            variant='link'
                            className='h-auto px-0 text-xs'
                            onClick={() => setPreviewMetadata(table)}
                          >
                            View metadata
                          </Button>
                          {canEdit && requiresResolution && datasetDetail && (
                            <Button
                              type='button'
                              size='sm'
                              variant='outline'
                              className='h-8 text-xs'
                              onClick={handleResolve}
                            >
                              Resolve issues
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className='space-y-3'>
            <h3 className='text-lg font-medium'>
              CSV files ({datasetDetail?.csv_object_keys.length || 0})
            </h3>
            <Table
              columns={datasetDetailColumns}
              data={datasetDetailRows}
              loading={datasetDetailLoading || datasetFilesLoading}
            />
          </div>
        </div>
      )}

      <Dialog
        open={Boolean(previewMetadata)}
        onOpenChange={(open) => {
          if (!open) {
            setPreviewMetadata(null);
          }
        }}
      >
        <DialogContent className='max-w-3xl'>
          <DialogHeader>
            <DialogTitle>Table metadata preview</DialogTitle>
          </DialogHeader>
          {previewMetadata && (
            <div className='max-h-[60vh] overflow-y-auto rounded-md border bg-muted/40 p-3'>
              <JsonViewer data={previewMetadata} maxHeight='50vh' />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={isCollectionInfoModalOpen}
        onOpenChange={(open) => {
          setIsCollectionInfoModalOpen(open);
        }}
      >
        <DialogContent className='max-w-3xl'>
          <DialogHeader>
            <DialogTitle>Collection info</DialogTitle>
          </DialogHeader>
          <p className='text-sm text-muted-foreground mb-3'>
            This snapshot shows the current vector storage configuration and
            health details reported by the connected Qdrant collection.
          </p>
          <div className='max-h-[60vh] overflow-y-auto rounded-md border bg-muted/40 p-3'>
            <JsonViewer data={collectionInfo || {}} maxHeight='50vh' />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isChatFiltersModalOpen}
        onOpenChange={(open) => {
          setIsChatFiltersModalOpen(open);
          if (!open) {
            setChatFiltersError(null);
          }
        }}
      >
        <DialogContent className='max-w-2xl'>
          <DialogHeader>
            <DialogTitle>Chat filters</DialogTitle>
          </DialogHeader>
          <p className='text-sm text-muted-foreground'>
            Provide an array of filter rules to auto-apply when specific
            keywords appear in chat questions. Each rule should include
            `keywords` (array), `key`, and `value`. Leave empty to remove all
            rules.
          </p>
          <Textarea
            value={chatFiltersText}
            onChange={(event) => {
              setChatFiltersText(event.target.value);
              setChatFiltersError(null);
            }}
            className='font-mono h-52'
            placeholder='[{"keywords":["responsive","request"],"key":"category","value":"Responsive Documents"}]'
          />
          {chatFiltersError && (
            <p className='text-sm text-destructive'>{chatFiltersError}</p>
          )}
          <div className='flex justify-end gap-2 pt-2'>
            <Button
              variant='outline'
              onClick={() => setIsChatFiltersModalOpen(false)}
              disabled={isChatFiltersSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveChatFilters}
              disabled={isChatFiltersSaving}
            >
              {isChatFiltersSaving ? 'Saving...' : 'Save filters'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <CollectionChatPromptModal
        open={isChatPromptModalOpen}
        onOpenChange={setIsChatPromptModalOpen}
        orgId={currentOrg?.orgid || null}
        collectionId={collectionId ?? null}
        collectionName={collection?.name || null}
        onPromptUpdated={handleChatPromptUpdated}
      />

      {isWordPressCollection && (
        <WordPressExclusionModal
          open={isWpExclusionModalOpen}
          onOpenChange={(open) => {
            setIsWpExclusionModalOpen(open);
            if (!open) setWpExclusionSiteId(null);
          }}
          collectionId={collectionId || ''}
          initialRules={wpExclusionModalRules}
          existingSourceConfig={
            (collection?.source_config || {}) as Record<string, unknown>
          }
          onRulesSaved={refetchCollection}
          siteId={wpExclusionSiteId || undefined}
          siteLabel={
            wpExclusionActiveSite?.label || wpExclusionActiveSite?.baseUrl
          }
          onSiteSave={
            wpExclusionSiteId ? handleSiteSaveExclusionRules : undefined
          }
        />
      )}

      <CsvPreviewDialog
        open={Boolean(previewCsv)}
        onOpenChange={(next) => {
          if (!next) {
            setPreviewCsv(null);
          }
        }}
        orgId={collection?.orgid || currentOrg?.orgid || ''}
        collectionId={collectionId || ''}
        s3Key={previewCsv?.key ?? null}
        filename={previewCsv?.filename}
      />

      <FilePreviewModal
        open={Boolean(filePreview)}
        onOpenChange={(next) => {
          if (!next) {
            setFilePreview(null);
          }
        }}
        orgId={collection?.orgid || currentOrg?.orgid}
        collectionId={collectionId}
        filename={filePreview?.filename || ''}
        contentType={filePreview?.contentType}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isBulkDeleteModalOpen}
        isLoading={isBulkDeleteLoading}
        title='Delete selected files'
        description={
          selectedFilesCount === 1
            ? 'Are you sure you want to delete this file? This action cannot be undone.'
            : selectedFilesCount > 1
              ? `Are you sure you want to delete ${selectedFilesCount} files? This action cannot be undone.`
              : 'Select at least one file to delete.'
        }
        buttonLabel={
          selectedFilesCount === 1
            ? 'Delete file'
            : selectedFilesCount > 1
              ? 'Delete files'
              : 'Close'
        }
        handleDelete={handleBulkDeleteModalDelete}
        handleCancel={() => setIsBulkDeleteModalOpen(false)}
      />

      {/* Single Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        isLoading={isDeleteLoading}
        title='Remove source'
        description='Are you sure you want to remove this source? This action cannot be undone.'
        buttonLabel='Remove source'
        handleDelete={handleDeleteSource}
        handleCancel={() => {
          setIsDeleteModalOpen(false);
          setSelectedSource(null);
        }}
      />

      {/* Remove WordPress Site Modal */}
      <DeleteConfirmationModal
        isOpen={pendingSiteRemoval !== null}
        isLoading={isRemovingSite}
        title='Remove WordPress site'
        description={`Remove "${pendingSiteRemoval?.label ?? 'this site'}" from this collection? This stops future ingestion for the site. This action cannot be undone.`}
        buttonLabel='Remove site'
        handleDelete={() => void handleConfirmRemoveSite()}
        handleCancel={() => setPendingSiteRemoval(null)}
      />

      {/* Collection Delete Modal */}
      <DeleteCollectionModal
        isOpen={isCollectionDeleteModalOpen}
        isLoading={isCollectionDeleteLoading}
        handleSoftDelete={handleDeleteCollection}
        handleHardDelete={handleHardDeleteCollection}
        handleCancel={() => {
          setIsCollectionDeleteModalOpen(false);
        }}
      />

      {/* New Data Source Modal */}
      <NewDataSourceModal
        collectionId={collectionId || ''}
        collection={collection}
        collectionDataType={collection?.data_type}
        isModalOpen={isNewDataSourceModalOpen}
        setIsModalOpen={setIsNewDataSourceModalOpen}
        setIsLoading={setLoading}
        sources={sources}
        setSources={setSources}
      />

      {/* Edit Collection Modal */}
      <EditCollectionModal
        isOpen={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        collection={collection}
        collectionId={collectionId}
        onCollectionUpdated={handleCollectionUpdated}
      />

      <ShareModal
        resourceType='collection'
        resourceId={collectionId ?? ''}
        orgId={currentOrg?.orgid ?? null}
        open={isShareModalOpen}
        onOpenChange={setIsShareModalOpen}
      />

      <EditFileFieldsModal
        isOpen={isEditFileFieldsModalOpen}
        onOpenChange={(open) => {
          setIsEditFileFieldsModalOpen(open);
          if (!open) setSelectedSource(null);
        }}
        collectionId={collectionId || ''}
        source={selectedSource}
        exposedAttributes={collection?.exposed_upload_attributes ?? []}
        exposedCardinality={
          collection?.exposed_upload_attribute_cardinality ?? null
        }
        onSaved={() => {
          fetchCollectionInfo();
        }}
      />

      <FileInfoModal
        isOpen={isFileInfoModalOpen}
        onOpenChange={(open) => {
          setIsFileInfoModalOpen(open);
          if (!open) setSelectedSource(null);
        }}
        collectionId={collectionId || ''}
        source={selectedSource}
        canReindex={canEdit}
        onReindexed={() => {
          fetchSources(true);
          fetchCollectionInfo();
        }}
      />

      {collection?.data_type === 'structured' && (
        <>
          <DatasetModal
            isOpen={isDatasetModalOpen}
            mode={datasetModalMode}
            initialValues={datasetModalInitialValues}
            csvOptions={datasetCsvOptions}
            datasets={datasets}
            collectionId={collectionId || ''}
            activeDatasetId={datasetDraft?.id}
            focusKey={datasetModalFocusKey}
            isSubmitting={isDatasetSubmitting}
            onClose={handleDatasetModalClose}
            onSubmit={handleDatasetSubmit}
            onRefreshSources={() => fetchSources(true)}
          />

          <DeleteConfirmationModal
            isOpen={Boolean(datasetDeleteTarget)}
            isLoading={isDatasetDeleting}
            title='Delete dataset'
            description='Are you sure you want to delete this dataset? This action cannot be undone.'
            buttonLabel='Delete dataset'
            handleDelete={handleDatasetDeleteConfirm}
            handleCancel={() => setDatasetDeleteTarget(null)}
          />
        </>
      )}
    </div>
  );
};
